type CommonLocale = typeof import('../en/common').default

const common = {
	title: '{{brandName}}',
	welcome: 'Bienvenido a {{brandName}}',
	diagram: 'Diagrama',
	language: 'Idioma',
	english: 'Inglés',
	spanish: 'Español',
	// Claves canónicas de CTA pública (PRD 039). Resueltas siempre a través de
	// app/utils/pz/cta/resolvePublicCtaKey.ts; no añadir variantes por
	// superficie.
	cta: {
		startFree: 'Empieza gratis',
		createAccountAndStart: 'Crea tu cuenta y empieza aquí',
		unlockCourse:
			'Desbloquear {{mainEntityDemonstrativeLower}} {{mainEntityLabelLower}}',
		unlockCollection:
			'Desbloquear {{collectionEntityDemonstrativeLower}} {{collectionEntityLabelLower}}',
	},
	docs: {
		onThisPage: 'En esta página',
		search: {
			placeholder: 'Busca por título...',
		},
		navigation: {
			open: 'Explorar páginas',
			noResults: 'No hay páginas que coincidan con tu búsqueda.',
		},
		public: {
			title: 'Centro de ayuda',
			groups: {
				startHere: 'Empieza aquí',
				coreConcepts: 'Conceptos clave',
				editorialSystem: 'Sistema editorial',
				pedagogicalPrinciples: 'Principios pedagógicos',
				productGuides: 'Guías de producto',
				commonErrors: 'Errores frecuentes',
			},
			relatedArticles: 'Artículos relacionados',
			semanticSearch: 'Búsqueda semántica',
			semanticSearchDescription:
				'Encuentra respuestas en toda la documentación con ranking por intención.',
			searchNoResults:
				'No hay páginas de documentación que coincidan con tu consulta.',
			searchHint:
				'Escribe una pregunta o palabras clave para buscar en el centro de ayuda.',
		},
		private: {
			searchPlaceholder: 'Busca docs internas...',
		},
		meta: {
			owner: 'Responsable',
			status: 'Estado',
			lastReviewed: 'Última revisión',
			statusDraft: 'Borrador',
			statusActive: 'Activo',
			statusDeprecated: 'Deprecado',
		},
		feedback: {
			titleA: '¿Esta página resolvió tu caso?',
			titleB: '¿Este artículo te ayudó en tu flujo de trabajo?',
			question:
				'Tu feedback nos ayuda a priorizar mejoras en la documentación.',
			helped: 'Sí, me ayudó',
			notHelped: 'Todavía no',
			thanks: 'Gracias por tu feedback.',
			contactCta: 'Contactar soporte para este caso',
		},
		dashboard: {
			title: 'Panel de salud de documentación pública',
			description:
				'Resumen operativo de vigencia, ownership y cobertura de intención de soporte.',
			generatedAt: 'Generado el',
			totalDocs: 'Total de documentos',
			docsWithoutOwner: 'Documentos sin responsable',
			staleDocs: 'Documentos vencidos',
			noSupportIntent: 'Documentos sin intención de soporte',
			noFeedbackData:
				'Las páginas de baja utilidad deben revisarse junto con docs_page_view, docs_scroll_depth y el comportamiento de búsqueda.',
			tableTitle: 'Estado detallado',
			table: {
				path: 'Ruta',
				source: 'Origen',
				owner: 'Responsable',
				status: 'Estado',
				lastReviewed: 'Última revisión',
				cadence: 'Cadencia (días)',
				freshness: 'Vigencia',
			},
			sourceBrand: 'Marca',
			sourceGlobal: 'Global',
			freshnessOk: 'Al día',
			freshnessStale: 'Requiere revisión',
			sopLink: 'Abrir SOP de revisión mensual',
			noData: 'No hay datos de documentación pública para esta marca.',
		},
	},
	changeLanguage: 'Cambiar idioma',
	settings: {
		title: 'Ajustes',
		general: 'General',
		description: 'Administra la configuración de tu cuenta.',
		mobileSelectPlaceholder: 'Selecciona una sección',
		tabs: {
			profile: 'Perfil',
			account: 'Cuenta',
			appearance: 'Apariencia',
			notifications: 'Notificaciones',
			billing: 'Facturación',
			display: 'Pantalla',
		},
		appearance: {
			description:
				'Tema y estilo del espacio privado de {{brandKey}}. La parte pública mantiene la identidad de marca.',
			sections: {
				theme: 'Modo de tema',
				stylePreset: 'Estilo',
			},
			themeOptions: {
				system: {
					title: 'Sistema',
					description: 'Sigue la preferencia del dispositivo.',
				},
				light: {
					title: 'Claro',
					description: 'Usa siempre el espacio en claro.',
				},
				dark: {
					title: 'Oscuro',
					description: 'Usa siempre el espacio en oscuro.',
				},
			},
			presetKinds: {
				default: 'Base de marca',
				alternative: 'Variante',
			},
			errors: {
				invalidStylePreset: 'Selecciona un estilo válido.',
			},
		},
		jurisdiction: {
			label: 'País del catálogo',
			placeholder: 'Selecciona país',
			help: 'Se usa para priorizar contenido nacional cuando exista.',
			submit: 'Guardar país del catálogo',
		},
		messages: {
			accountUpdated: 'Los ajustes de la cuenta se han actualizado.',
			profileUpdated: 'El perfil se ha actualizado.',
			notificationsUpdated: 'Las notificaciones se han actualizado.',
			displayUpdated: 'Los ajustes de pantalla se han actualizado.',
			appearanceUpdated: 'La apariencia se ha actualizado.',
			accountUpdateFailed:
				'No se pudieron actualizar los ajustes de la cuenta.',
			profileUpdateFailed: 'No se pudo actualizar el perfil.',
		},
		profile: {
			title: 'Perfil',
			description:
				'Actualiza la información de tu perfil. Esta información puede mostrarse públicamente.',
			valueUnavailable: 'No disponible',
			planFree: 'Gratis',
			planPro: 'Pro',
			basicInfo: {
				title: 'Información básica',
				description:
					'Datos de referencia de tu cuenta. Estos campos no se pueden editar aquí.',
				displayName: 'Nombre visible',
				username: 'Nombre de usuario',
				email: 'Correo electrónico',
				memberSince: 'Miembro desde',
				plan: 'Plan',
			},
			form: {
				displayNameLabel: 'Nombre visible',
				displayNamePlaceholder: 'Tu nombre visible',
				bioLabel: 'Biografía',
				bioPlaceholder: 'Cuéntanos un poco sobre ti',
				bioHint:
					'Puedes usar @menciones para enlazar a otros usuarios u organizaciones.',
				avatarUrlLabel: 'URL del avatar',
				avatarUrlPlaceholder: 'https://example.com/avatar.jpg',
				submit: 'Actualizar perfil',
			},
		},
	},
	all: 'Todos',
	home: 'Inicio',
	about: 'Acerca de',
	cancel: 'Cancelar',
	yes: 'Sí',
	no: 'No',
	loading: 'Cargando…',
	processing: 'Procesando…',
	footer: {
		copyright: '  {{year}} {{brandName}}. Todos los derechos reservados.',
	},
	cookies: {
		message:
			'Este sitio web utiliza cookies para mejorar la experiencia del usuario. Consulta nuestra',
		privacyPolicy: 'Política de privacidad',
		forMoreInfo: 'para más información.',
		accept: 'Aceptar',
		decline: 'Rechazar',
	},
	error: {
		title: 'Error',
		unexpected: '¡Vaya! Algo salió mal.',
		unknown: 'Error desconocido',
		noDetails: 'No hay detalles adicionales disponibles.',
		dialogNotFound: 'Diálogo "{{dialogName}}" no encontrado',
		boundary: {
			databaseError:
				'Hemos encontrado un error en la base de datos. Por favor, inténtalo de nuevo o contacta con soporte si el problema persiste.',
			serverError:
				'Algo salió mal de nuestra parte. Nuestro equipo ha sido notificado. Por favor, inténtalo más tarde.',
			validationError:
				'La información proporcionada no es válida. Por favor, verifica tu entrada e inténtalo de nuevo.',
			defaultError:
				'Ocurrió un error inesperado. Por favor, inténtalo de nuevo o contacta con soporte si el problema continúa.',
			showTechnical: 'Mostrar detalles técnicos',
			showFriendly: 'Mostrar mensaje amigable',
		},
	},
	notFound: {
		title: 'Error 404',
		message: 'No hemos encontrado esta página.',
	},
	permissionDenied: {
		title: 'Error 403',
		message: 'No tienes permisos para realizar esta acción',
	},
	backToHome: 'Volver a la página de inicio',
	toggle: 'Alternar',
	selected: 'Seleccionados',
	confirmDelete: '¿Estás seguro de que quieres eliminar estos elementos?',
	success: 'Éxito',
	fields: {
		createdAt: 'Creado',
		updatedAt: 'Actualizado',
	},
	table: {
		noResults: 'No se encontraron resultados',
		search: 'Buscar...',
		filter: 'Filtrar elementos',
		rows: 'Filas',
		rowsPerPage: 'Filas por página',
		goTo: 'Ir a',
		page: 'Página',
		of: 'De',
		previous: 'Anterior',
		next: 'Siguiente',
		first: 'Primera',
		last: 'Última',
		actions: 'Acciones',
		filters: {
			all: 'Todos',
			active: 'Activo',
			inactive: 'Inactivo',
			published: 'Publicado',
			draft: 'Borrador',
		},
		sort: {
			asc: 'Ordenar ascendente',
			desc: 'Ordenar descendente',
			none: 'Sin ordenar',
		},
	},
	buttons: {
		create: 'Crear',
	},
	actions: {
		create: 'Crear',
		edit: 'Editar',
		delete: 'Eliminar',
		suspendAccount: 'Suspender cuenta',
		reactivateAccount: 'Reactivar cuenta',
		hardDeleteAccount: 'Borrado irreversible de cuenta',
		impersonate: 'Suplantar',
		view: 'Ver',
		save: 'Guardar',
		saving: 'Guardando...',
		cancel: 'Cancelar',
		confirm: 'Confirmar',
		close: 'Cerrar',
		back: 'Atrás',
		next: 'Siguiente',
		previous: 'Anterior',
		expand: 'Expandir',
		collapse: 'Contraer',
		submit: 'Enviar',
		reset: 'Restablecer',
		clear: 'Limpiar',
		search: 'Buscar',
		filter: 'Filtrar',
		export: 'Exportar',
		import: 'Importar',
		refresh: 'Actualizar',
		retry: 'Reintentar',
		duplicate: 'Duplicar',
		archive: 'Archivar',
		restore: 'Restaurar',
		publish: 'Publicar',
		unpublish: 'Despublicar',
		toggleFavorite: 'Marcar como favorito',
		toggleUnfavorite: 'Quitar de favoritos',
		togglePinned: 'Fijar',
		toggleUnpinned: 'Desfijar',
		bookmarkResource: 'Guardar contenido',
		markCompleted: 'Marcar como completado',
		markIncomplete: 'Marcar como incompleto',
	},
	views: {
		list: 'Lista',
		grid: 'Tarjetas',
		kanban: 'Kanban',
	},
	status: {
		active: 'Activo',
		inactive: 'Inactivo',
		published: 'Publicado',
		draft: 'Borrador',
		archived: 'Archivado',
		pending: 'Pendiente',
		approved: 'Aprobado',
		rejected: 'Rechazado',
		completed: 'Completado',
		inProgress: 'En progreso',
		notStarted: 'No iniciado',
		bookmarked: 'Guardado',
		suspended: 'Suspendida',
	},
	filters: {
		favorites: 'Favoritos',
		bookmarks: 'Guardados',
	},
	messages: {
		success: {
			created: '{{entity}} creado exitosamente',
			updated: '{{entity}} actualizado exitosamente',
			deleted: '{{entity}} eliminado exitosamente',
			saved: 'Cambios guardados exitosamente',
			published: '{{entity}} publicado exitosamente',
			unpublished: '{{entity}} despublicado exitosamente',
		},
		error: {
			create: 'Error al crear {{entity}}',
			update: 'Error al actualizar {{entity}}',
			delete: 'Error al eliminar {{entity}}',
			save: 'Error al guardar cambios',
			load: 'Error al cargar datos',
			network: 'Error de red',
			validation: 'Por favor revisa tu entrada e intenta de nuevo',
			permission: 'No tienes permisos para realizar esta acción',
		},
		confirm: {
			delete: '¿Estás seguro de que quieres eliminar este {{entity}}?',
			archive: '¿Estás seguro de que quieres archivar este {{entity}}?',
			publish: '¿Estás seguro de que quieres publicar este {{entity}}?',
			unpublish: '¿Estás seguro de que quieres despublicar este {{entity}}?',
		},
	},
	validation: {
		required: 'Este campo es obligatorio',
		positiveNumber: 'Debe ser un número positivo',
	},
	collections: {
		title: '{{collectionEntityLabelPlural}}',
		description:
			'Explora {{collectionEntityLabelPluralLower}} construid{{collectionEntityGenderSuffixPlural}} a partir de {{mainEntityLabelPluralLower}}',
		myCollections: '{{collectionEntityLabelPlural}}',
		myCollectionsDescription:
			'Gestiona tus {{collectionEntityLabelPluralLower}}',
		sequential: 'Secuencial',
		noCollections: 'No hay {{collectionEntityLabelPluralLower}} disponibles',
		noCollectionsYet: 'Aún no tienes {{collectionEntityLabelPluralLower}}',
		createFirstCollection:
			'Crea tu primer {{collectionEntityLabelLower}} para empezar',
		noCourses:
			'No hay {{mainEntityLabelPluralLower}} en este {{collectionEntityLabelLower}} todavía',
		listView: 'Vista de lista',
		diagramView: 'Diagrama',
		diagramComingSoon: 'Vista de diagrama próximamente',
		diagramDescription:
			'Visualiza {{collectionEntityArticleLower}} {{collectionEntityLabelLower}} con un diagrama interactivo',
		collectionsCarousel: {
			title:
				'{{collectionEntityLabelPlural}} destacad{{collectionEntityGenderSuffixPlural}}',
			subtitle:
				'{{collectionEntityLabelPlural}} seleccionad{{collectionEntityGenderSuffixPlural}} para desarrollar tus habilidades',
			viewAll: 'Explorar {{collectionEntityLabelPluralLower}}',
			startFree: 'Empieza gratis',
			viewFreeCollection: 'Ver {{collectionEntityLabelLower}} gratis',
			viewCollection: 'Ver {{collectionEntityLabelLower}}',
			previousSlide: 'Diapositiva anterior',
			nextSlide: 'Siguiente diapositiva',
			goToSlide: 'Ir a la diapositiva {{slide}}',
			previewAlt:
				'Vista previa {{collectionEntityArticleWithDeLower}} {{collectionEntityLabelLower}}',
			modules_one: '{{count}} módulo',
			modules_other: '{{count}} módulos',
			courses_one: '{{count}} {{mainEntityLabelLower}}',
			courses_other: '{{count}} {{mainEntityLabelPluralLower}}',
			routes_one: '{{count}} {{collectionEntityLabelLower}}',
			routes_other: '{{count}} {{collectionEntityLabelPluralLower}}',
		},
	},
	studyPanel: {
		title: 'Panel de estudio',
		searchPlaceholder:
			'Buscar {{collectionEntityLabelPluralLower}} y {{mainEntityLabelPluralLower}}',
		scope: {
			panel: 'Mi biblioteca',
			catalog: 'Catálogo {{brandShortName}}',
		},
		catalog: {
			emptyDashboard: {
				title: 'Empieza explorando el catálogo',
				description:
					'Cuando te inscribas en {{mainEntityArticleLower}} {{mainEntityLabelLower}} o en {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}, este aviso desaparecerá, y los verás en tu biblioteca personal.',
			},
			empty: {
				title: 'No hay resultados en el catálogo',
				description: 'Prueba con otra búsqueda o cambia los filtros activos.',
			},
			table: {
				learning: 'Contenido',
				structure: 'Estructura',
				action: 'Acción',
			},
			belongsTo: 'Dentro de',
			contextOnlyProgram: 'Contexto',
			structure: {
				program: '{{count}} {{mainEntityLabelPluralLower}}',
				course: '{{count}} temas',
			},
			actions: {
				enrollProgram:
					'Empezar {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
				enrollCourse:
					'Empezar {{mainEntityDemonstrativeLower}} {{mainEntityLabelLower}}',
				enrollCourseOnly:
					'Inscribirme solo en {{mainEntityArticleLower}} {{mainEntityLabelLower}}',
			},
		},
		badges: {
			course: '{{mainEntityLabel}}',
			roadmap: '{{collectionEntityLabel}}',
			sequential: 'Secuencial',
			goalOriented: 'Orientado a objetivos',
			exploratory: 'Exploratorio',
			certificate: 'Certificado',
		},
		views: {
			list: 'Lista',
			grid: 'Tarjetas',
		},
		actions: {
			open: 'Abrir',
			details: 'Detalles',
		},
		deleteConfirm: {
			courseTitle: '¿Eliminar {{mainEntityLabel}}?',
			courseDescription:
				'"{{title}}" se eliminará de forma permanente. Esta acción no se puede deshacer.',
			courseImpact:
				'Se eliminarán las inscripciones y el progreso vinculados a {{mainEntityArticleLower}} {{mainEntityLabelLower}}.',
			collectionTitle: '¿Eliminar {{collectionEntityLabel}}?',
			collectionDescription:
				'"{{title}}" se eliminará de forma permanente. Esta acción no se puede deshacer.',
			collectionDescriptionWithCounts:
				'"{{title}}" se eliminará de forma permanente, incluyendo sus {{courseCount}} {{mainEntityLabelPluralLower}} vinculad{{mainEntityGenderSuffixPlural}}.',
			collectionImpact:
				'Se eliminará la inscripción de este {{collectionEntityLabel}} y perderás acceso.',
			roadmapTitle: '¿Quitar {{collectionEntityLabel}} de tu panel?',
			roadmapDescription: '"{{title}}" se quitará de tu panel de estudio.',
			roadmapImpact: 'Podrás volver a inscribirte más tarde, si lo deseas.',
			roadmapConfirmAction: 'Quitar del panel',
			confirmAction: 'Eliminar permanentemente',
		},
		filters: {
			all: 'Todos',
			allTypes: 'Todos los tipos',
			courses: '{{mainEntityLabelPlural}}',
			roadmaps: '{{collectionEntityLabelPlural}}',
			allCategories: 'Todas las categorías',
			allProgress: 'Todo el progreso',
			notStarted: 'No iniciado',
			inProgress: 'En progreso',
			paused: 'Pausado',
			completed: 'Completado',
			archived: 'Archivado',
		},
		units: {
			modules_one: '{{count}} módulo',
			modules_other: '{{count}} módulos',
			topics_one: '{{count}} tema',
			topics_other: '{{count}} temas',
			courses_one: '{{count}} {{mainEntityLabelLower}}',
			courses_other: '{{count}} {{mainEntityLabelPluralLower}}',
		},
		table: {
			item: 'Nombre',
			type: 'Tipo',
			status: 'Estado',
			category: 'Categoría',
			lastActivity: 'Última\u00A0actividad',
			deadline: 'Fecha límite',
			progress: 'Progreso',
			enrolledAt: 'Inscrito',
		},
		drawer: {
			description:
				'Contexto ampliado y controles rápidos para este elemento de estudio.',
			progress: 'Progreso',
			enrolledAt: 'Inscrito',
			learningUnits: 'Unidades de aprendizaje',
			category: 'Categoría',
			completedCourses:
				'{{mainEntityLabelPlural}} completad{{mainEntityGenderSuffixPlural}}',
			childCourses:
				'{{mainEntityLabelPlural}} {{collectionEntityArticleWithDeLower}} {{collectionEntityLabelLower}}',
			moreCourses: 'más',
			roadmapStatus:
				'Estado {{collectionEntityArticleWithDeLower}} {{collectionEntityLabelLower}}',
			updateRoadmapStatus: 'Actualizar estado',
			courseActions:
				'Acciones {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
		},
		empty: {
			title: 'No hay elementos que coincidan',
			description:
				'Prueba a cambiar los filtros o la búsqueda para encontrar tus {{mainEntityLabelPluralLower}} y {{collectionEntityLabelPluralLower}}.',
		},
	},
	collectionDetail: {
		hero: {
			guidedLabel:
				'{{collectionEntityLabel}} guiad{{collectionEntityGenderSuffix}}',
			referenceLabel: '{{collectionEntityLabel}} de referencia',
			estimatedHours: '{{count}} horas estimadas',
		},
		actions: {
			backToDashboard: 'Volver al panel',
			toggleMode: 'Cambiar modo de aprendizaje',
			enroll:
				'Inscribirme en {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
			enrollCta: 'Inscribirme',
			startGoalPath: 'Empezar tu {{collectionEntityLabelLower}}',
			continueGoalPath: 'Continuar tu {{collectionEntityLabelLower}}',
			start: 'Empezar',
			continue: 'Continuar',
			review: 'Repasar',
			pause: 'Pausar',
			showTopics: 'Mostrar temas',
			hideTopics: 'Ocultar temas',
		},
		badges: {
			enrolled: 'Inscrito',
		},
		modes: {
			goalOriented: 'Orientado a objetivos',
			exploratory: 'Exploratorio',
		},
		views: {
			grid: 'Tarjetas',
			roadmap: 'Roadmap',
		},
		roadmap: {
			recommended: 'Siguiente recomendado',
			moduleProgress:
				'{{completed}}/{{total}} {{mainEntityLabelPluralLower}} completad{{mainEntityGenderSuffixPlural}}',
		},
		modeDescriptions: {
			goalOriented:
				'Sigue {{mainEntityArticleLower}} {{mainEntityLabelLower}} estructurad{{mainEntityGenderSuffix}} con una secuencia recomendada para alcanzar un objetivo de aprendizaje claro.',
			exploratory:
				'Descubre temas con libertad y elige el orden que mejor encaje con tu curiosidad y tus necesidades.',
		},
		metrics: {
			progress: 'Progreso',
			courses: '{{mainEntityLabelPlural}}',
			topics: 'Temas',
			modules: 'Módulos',
			stages: 'etapas',
			topicProgress: '{{completed}}/{{total}} temas completados',
			noTopics: 'Sin temas todavía',
		},
		resume: {
			continueEyebrow: 'Continúa donde lo dejaste',
			startEyebrow:
				'Empieza {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
		},
		status: {
			not_enrolled: 'No inscrito',
			not_started: 'No iniciado',
			in_progress: 'En progreso',
			paused: 'Pausado',
			completed: 'Completado',
		},
		empty: {
			noCourses:
				'Todavía no hay {{mainEntityLabelPluralLower}} disponibles en este {{collectionEntityLabelLower}}.',
		},
		finalCta: {
			title: '¿Listo para empezar?',
			subtitleLoggedIn: 'Inscríbete en {{collection}} para comenzar ahora.',
			titleEnrolled: 'Continúa tu {{collectionEntityLabelLower}}',
			subtitleEnrolled:
				'Retoma {{collection}} y sigue avanzando donde lo dejaste.',
			enrollButton:
				'Empezar {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
			enrollButtonAnonymous: 'Crea tu cuenta y empieza aquí',
			anonymousHint:
				'Es gratis. Te pediremos crear una cuenta para guardar tu progreso.',
			continueButton:
				'Continuar {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
		},
		actionError: {
			title: 'No hemos podido empezar esto',
		},
		fallbackCollectionTitle: '{{collectionEntityLabel}} sin título',
		fallbackModuleTitle: 'Módulo sin título',
		fallbackCourseTitle: '{{mainEntityLabel}} sin título',
		fallbackTopicTitle: 'Tema sin título',
		fallbackDescription:
			'No hay descripción disponible para este {{collectionEntityLabelLower}}.',
		fallbackCourseDescription:
			'No hay descripción disponible para este {{mainEntityLabelLower}}.',
	},
	upgradeAction: {
		limitReached: {
			title: 'Has agotado tus accesos incluidos',
			description:
				'Tu biblioteca sigue disponible. Para abrir nuev{{mainEntityGenderSuffix}}s {{mainEntityLabelPluralLower}}, suscríbete a Pro.',
			primaryCta: 'Revisar mi acceso',
		},
	},
	courses: '{{mainEntityLabelPlural}}',
	students: 'Estudiantes',
	section: 'Sección',
	module: 'Módulo',
	stage: 'Etapa',
	step: 'Paso',
	viewDetails: 'Ver detalles',
	viewCourse: 'Ver {{mainEntityLabelLower}}',
	view: 'Ver',
	open: 'Abrir',
	draft: 'Borrador',
	convert: 'Convertir',
	languages: {
		en: 'Inglés',
		es: 'Español',
		fr: 'Francés',
		de: 'Alemán',
		it: 'Italiano',
		pt: 'Portugués',
		english: 'Inglés',
		spanish: 'Español',
		french: 'Francés',
		german: 'Alemán',
		italian: 'Italiano',
		portuguese: 'Portugués',
	},
	levels: {
		beginner: 'Principiante',
		intermediate: 'Intermedio',
		advanced: 'Avanzado',
		expert: 'Experto',
		all: 'Todos los niveles',
	},
	accessLevels: {
		free: 'Gratis',
		subscription: 'Suscripción',
		enrollment: 'Inscripción',
		preview: 'Vista previa',
	},
	i18n: {
		settings: {
			defaultLanguage: 'Idioma predeterminado',
			supportedLngs: 'Idiomas soportados',
		},
	},
	subscription: {
		proOnly: 'Disponible en el plan Pro',
	},
	impersonation: {
		banner: 'Suplantando a {{target}}',
		exit: 'Salir de suplantación',
	},
	editor: {
		placeholder: 'Escribe tu contenido aquí...',
	},
	courseMap: {
		insertionPoint: {
			createTopic: 'Crear nuevo tema',
			searchEntity: 'Buscar contenido existente',
		},
		module: {
			rename: 'Renombrar módulo',
			toggle: 'Expandir o colapsar módulo',
			createNew: 'Añadir nuevo módulo',
		},
		topic: {
			rename: 'Renombrar tema',
			completeTopic: 'Completar tema',
			label: 'Tema',
			markCompleted: 'Marcar como completado',
			markNotStarted: 'Marcar como no empezado',
		},
	},
	tour: {
		next: 'Siguiente',
		previous: 'Atrás',
		done: 'Entendido',
		progressSeparator: 'de',
		dashboard: {
			scope: {
				title: 'Tu panel y el catálogo',
				description:
					'Alterna entre tu panel de estudio personal y nuestro catálogo de {{mainEntityLabelPluralLower}} desde aquí.',
			},
			libraryScope: {
				title: 'Mi biblioteca',
				description:
					'Busca aquí cuando quieras encontrar los {{collectionEntityLabelPluralLower}} y {{mainEntityLabelPluralLower}} que ya forman parte de tu aprendizaje.',
			},
			catalogScope: {
				title: 'Catálogo {{brandShortName}}',
				description:
					'Busca aquí para descubrir nuevos {{mainEntityLabelPluralLower}} y {{collectionEntityLabelPluralLower}} de nuestro catálogo completo.',
			},
			toolbar: {
				title: 'Busca y filtra',
				description:
					'Encuentra rápido tus {{mainEntityLabelPluralLower}} y {{collectionEntityLabelPluralLower}} por texto, tipo o categoría.',
			},
			create: {
				title:
					'Crea un{{mainEntityGenderSuffix}} {{mainEntityLabelLower}} propi{{mainEntityGenderSuffix}}',
				description:
					'Crea tu propi{{mainEntityGenderSuffix}} {{mainEntityLabelLower}} cuando necesites algo a medida.',
			},
			sidebarNav: {
				title: 'Navegación principal',
				description:
					'La barra lateral te lleva a tu biblioteca, tu actividad y a otras herramientas de interés.',
			},
			sidebarSupport: {
				title: 'Soporte y este tour',
				description:
					'Aquí viven la documentación, el contacto y la opción de repetir este tour.',
			},
		},
		course: {
			header: {
				title: 'De un vistazo',
				description:
					'Aquí ves el título de {{mainEntityDemonstrativeLower}} {{mainEntityLabelLower}} y las acciones principales.',
			},
			index: {
				title: 'El mapa de estudio',
				description:
					'Este índice organiza {{mainEntityArticleLower}} {{mainEntityLabelLower}} en módulos y temas. Es tu mapa de estudio.',
			},
			module: {
				title: 'Módulos',
				description:
					'Haz clic en un módulo para desplegar u ocultar sus temas. También puedes reordenarlos.',
			},
			topicRow: {
				title: 'Temas',
				description:
					'Entra en un tema para estudiarlo: contenidos, notas, y recursos externos.',
			},
			topicComplete: {
				title: 'Registra tu progreso',
				description:
					'Marca cada tema como completado para llevar el control de tu avance.',
			},
			topicActions: {
				title: 'Acciones del tema',
				description:
					'Al pasar el ratón sobre un tema aparece este menú con sus acciones: crear contenido, recopilar recursos y más.',
			},
			topicMeta: {
				title: 'Indicadores del tema',
				description:
					'Estas insignias te permiten navegar a contenidos y recursos.',
			},
			moduleInsertion: {
				title: 'Añade o reutiliza',
				description:
					'Cada módulo termina con dos botones: crea un tema nuevo o busca temas y {{mainEntityLabelPluralLower}} existentes para enlazarlos.',
			},
		},
	},
} satisfies CommonLocale

export default common
